

/**
 * UnsavedChangesDialog
 *
 * Polished, app-styled replacement for `window.confirm` when the user tries
 * to navigate away with dirty form state. Uses Radix AlertDialog (no close-X
 * auto-injected, proper focus trap, decision-required semantics).
 *
 * Offers 3 explicit actions:
 *   - Cancel             → closes dialog, stays put (left-aligned, low-emphasis)
 *   - Discard Changes    → red destructive button (middle)
 *   - Save & Continue    → primary filled button (right, default focus)
 *
 * Why not window.confirm? Browsers with "prevent additional dialogs"
 * silently suppress Cancel. An in-app dialog is always reliable.
 */
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Loader2, Save, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';

export const UnsavedChangesDialog = ({
  open,
  onOpenChange,
  title = 'Unsaved changes',
  description = 'You have unsaved changes. What would you like to do?',
  onSave,          // async () => void
  onDiscard,       // () => void
  onContinue,      // () => void  (runs after Save or Discard resolves)
  saveLabel = 'Save & Continue',
  discardLabel = 'Discard Changes',
  cancelLabel = 'Cancel',
}) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave();
      onContinue?.();
      onOpenChange?.(false);
    } catch (err) {
      console.error('UnsavedChangesDialog save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    onDiscard?.();
    onContinue?.();
    onOpenChange?.(false);
  };

  const handleCancel = () => {
    onOpenChange?.(false);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !saving) handleCancel();
        else if (!saving) onOpenChange?.(v);
      }}
    >
      <AlertDialogContent
        className="sm:max-w-[460px] p-0 overflow-hidden"
        data-testid="unsaved-changes-dialog"
      >
        {/* Body */}
        <div className="px-6 pt-6 pb-4">
          <AlertDialogHeader className="space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <AlertDialogTitle className="text-base font-semibold">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </div>

        {/* Footer — 3-button layout with clear hierarchy. Reversed on mobile so
            primary (Save) appears first/top. All buttons same height via size="sm". */}
        <AlertDialogFooter
          className={cn(
            "flex-col-reverse gap-2 border-t bg-muted/30 px-6 py-4",
            "sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:space-x-0"
          )}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={saving}
            className="h-9 px-4 font-medium text-muted-foreground hover:text-foreground"
            data-testid="unsaved-cancel-btn"
          >
            {cancelLabel}
          </Button>

          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDiscard}
              disabled={saving}
              className="h-9 px-4 font-medium border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"
              data-testid="unsaved-discard-btn"
            >
              {discardLabel}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              autoFocus
              className="h-9 px-4 font-medium gap-1.5"
              data-testid="unsaved-save-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saveLabel}
            </Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default UnsavedChangesDialog;
